//@flow
import {useCallback, useEffect, useState} from 'react';

import {
  getConversationNotesService,
  saveConversationNotesService,
} from './promises';
import {NOTE_MAX_CHARACTERS} from './constants';

export function useConversationNotes(conversationId: ?string | number) {
  const [content, setContent] = useState('');
  const [draft, setDraft] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchNote = useCallback(async () => {
    if (!conversationId) {
      setContent('');
      setDraft('');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const noteContent = await getConversationNotesService(conversationId);
      setContent(noteContent);
      if (!isEditing) {
        setDraft(noteContent);
      }
    } catch (e) {
      setError('No se pudo cargar la nota');
    } finally {
      setLoading(false);
    }
  }, [conversationId, isEditing]);

  useEffect(() => {
    setIsEditing(false);
    setDraft('');
    fetchNote();
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEditing = useCallback(() => {
    setDraft(content);
    setIsEditing(true);
  }, [content]);

  const cancelEditing = useCallback(() => {
    setDraft(content);
    setIsEditing(false);
  }, [content]);

  const updateDraft = useCallback((value: string) => {
    setDraft(value.slice(0, NOTE_MAX_CHARACTERS));
  }, []);

  const saveNote = useCallback(async () => {
    if (!conversationId) return false;

    setSaving(true);
    setError('');
    try {
      const savedContent = await saveConversationNotesService(
        conversationId,
        draft.trim()
      );
      const nextContent = savedContent || draft.trim();
      setContent(nextContent);
      setDraft(nextContent);
      setIsEditing(false);
      return true;
    } catch (e) {
      setError('No se pudo guardar la nota');
      return false;
    } finally {
      setSaving(false);
    }
  }, [conversationId, draft]);

  return {
    content,
    draft,
    isEditing,
    loading,
    saving,
    error,
    startEditing,
    cancelEditing,
    updateDraft,
    saveNote,
    maxCharacters: NOTE_MAX_CHARACTERS,
  };
}
